/**
 * #144 — Guard anti-loop del callback de login.
 *
 * Cuando el callback falla porque se perdió el `code_verifier` (Safari/ITP,
 * incógnito, cookies de terceros bloqueadas, otra tab), reiniciar el login es
 * lo correcto: normalmente el segundo intento funciona. Pero el reintento tiene
 * que ser UNO SOLO, y ahí estaba el bug: el flag vivía en `useState`, que se
 * pierde justo cuando el navegador se va a Cognito. Como Cognito mantiene la
 * sesión SSO y devuelve al instante, la página se montaba de cero con el flag
 * de nuevo en false y el ciclo no terminaba nunca.
 *
 * Por eso el flag va en sessionStorage: sobrevive al redirect y muere al cerrar
 * la pestaña, que es exactamente el alcance que queremos.
 */

export const RETRY_FLAG_KEY = 'zen.auth.callbackRetried';

/** Sólo este error se recupera reiniciando el flujo; el resto se le muestra al usuario. */
const RECOVERABLE = 'callback.missingVerifier';

/**
 * ¿Corresponde reiniciar el login automáticamente por este error?
 * Devuelve false si ya se reintentó en esta pestaña, cortando el loop.
 */
export function shouldAutoRetry(errorMessage: string, storage: Storage = sessionStorage): boolean {
  if (errorMessage !== RECOVERABLE) return false;
  return !hasRetried(storage);
}

export function hasRetried(storage: Storage = sessionStorage): boolean {
  try {
    return storage.getItem(RETRY_FLAG_KEY) === '1';
  } catch {
    // Si ni siquiera podemos leer sessionStorage no hay forma de garantizar el
    // corte del loop, así que asumimos que ya se reintentó y mostramos el error.
    return true;
  }
}

export function markRetried(storage: Storage = sessionStorage): void {
  try {
    storage.setItem(RETRY_FLAG_KEY, '1');
  } catch {
    /* storage bloqueado: hasRetried() ya devuelve true en ese caso */
  }
}

/** Se llama tras un login exitoso, para no bloquear un reintento legítimo futuro. */
export function clearRetryFlag(storage: Storage = sessionStorage): void {
  try {
    storage.removeItem(RETRY_FLAG_KEY);
  } catch {
    /* no-op */
  }
}
